import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { StatusLabel } from '@/components/generals/status-label'
import { BuckyMascot } from '@/components/generals/bucky-mascot'
import { ConceptHowItWorks } from '@/components/landing/concept-how-it-works'
import { ConceptExperience } from '@/components/landing/concept-experience'

import {
    ConceptDifference,
    ConceptIndustries,
    ConceptPlatform,
    ConceptProblem
} from '@/components/landing/concept-parts'

import { concept } from '@/constants/content'

const cardSpans = ['lg:col-span-2', 'lg:col-span-2', 'lg:col-span-2', 'lg:col-span-3', 'lg:col-span-3']

// "What is Bucky AI?": the full product explanation, from the overview to the bigger platform vision.
const ConceptSection: FC = (): ReactNode => {
    return (
        <section id='concept' aria-labelledby='concept-heading' className='relative overflow-hidden py-24 sm:py-32'>
            <div aria-hidden className='absolute top-20 -left-40 size-[32rem] rounded-full bg-navy-500/20 blur-[120px]' />

            <div className='container-site relative'>
                <div className='reveal flex flex-wrap items-center gap-3'>
                    <p className='text-xs font-semibold tracking-[0.18em] text-orange uppercase'>
                        {concept.eyebrow}
                    </p>

                    <StatusLabel status='inDevelopment' />
                </div>

                <div className='mt-6 grid gap-12 lg:grid-cols-[1.15fr_0.85fr] lg:items-center'>
                    <div className='reveal'>
                        <h2
                            id='concept-heading'
                            className='text-[2.4rem] leading-[1.04] font-bold tracking-[-0.03em] text-soft-white sm:text-6xl'
                        >
                            {concept.headlineLead}{' '}
                            <span className='block text-orange'>
                                {concept.headlineEmphasis}
                            </span>
                        </h2>

                        <p className='mt-7 border-l-2 border-orange pl-4 font-heading text-xl font-semibold text-soft-white sm:text-2xl'>
                            {concept.statement}
                        </p>

                        <p className='mt-6 max-w-2xl text-lg leading-relaxed text-cool-grey-light sm:text-xl'>
                            {concept.lead}
                        </p>
                    </div>

                    <div className='reveal relative mx-auto w-full max-w-md'>
                        <div className='relative grid aspect-square place-items-center overflow-hidden rounded-[2rem] bg-[radial-gradient(70%_70%_at_50%_40%,#1B3350,#0D1C2D_70%)] ring-1 ring-soft-white/10'>
                            <div aria-hidden className='absolute size-[70%] rounded-full border border-orange/25' />
                            <div aria-hidden className='absolute size-[92%] rounded-full border border-dashed border-soft-white/10' />
                            <div aria-hidden className='absolute bottom-[18%] size-[46%] rounded-full bg-orange/20 blur-3xl' />

                            <BuckyMascot size={200} title='Bucky, the BUCKY AI digital promoter' className='relative size-[46%]' />
                        </div>

                        <ul className='mt-4 flex flex-wrap justify-center gap-2'>
                            {concept.highlights.map(highlight => (
                                <li key={highlight} className='rounded-full border border-soft-white/15 bg-navy-900/70 px-3 py-1.5 text-xs font-medium text-soft-white/85'>
                                    {highlight}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <nav aria-label='What is Bucky AI, in this section' className='no-scrollbar mt-14 -mx-5 overflow-x-auto px-5 sm:mx-0 sm:px-0'>
                    <ul className='flex w-max gap-2 sm:w-auto sm:flex-wrap'>
                        {concept.subnav.slice(1).map(item => (
                            <li key={item.id}>
                                <a
                                    href={`#${item.id}`}
                                    className='inline-flex min-h-11 items-center rounded-full border border-soft-white/15 bg-navy-900/60 px-4 text-sm font-medium whitespace-nowrap text-cool-grey-light transition hover:border-orange/50 hover:text-soft-white'
                                >
                                    {item.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-6'>
                    {concept.description.map((item, index) => (
                        <article
                            key={item.kicker}
                            className={cn('reveal rounded-xl border border-soft-white/10 bg-navy-900/50 p-6', cardSpans[index])}
                        >
                            <h3 className='text-xs font-semibold tracking-[0.16em] text-orange uppercase'>
                                {item.kicker}
                            </h3>

                            <p className='mt-3 leading-relaxed text-soft-white/90'>
                                {item.text}
                            </p>
                        </article>
                    ))}
                </div>

                <ConceptProblem />
                <ConceptHowItWorks />
                <ConceptDifference />
                <ConceptIndustries />
                <ConceptExperience />
                <ConceptPlatform />
            </div>
        </section>
    )
}

export default ConceptSection
